import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    InputAdornment,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    TablePagination,
    InputBase
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    AttachMoney as MoneyIcon,
    History as HistoryIcon,
    LiveTv as LiveTvIcon,
    Settings as SettingsIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    NewReleases as NewIcon,
    ManageAccounts as ManageIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

function TvPackagePricingV2() {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [priceTable, setPriceTable] = useState({});
    const [addonPricing, setAddonPricing] = useState({});
    const [channels, setChannels] = useState([]);
    const [themePacks, setThemePacks] = useState([]);
    const [editingPackage, setEditingPackage] = useState(null);
    const [editingAddon, setEditingAddon] = useState(false);
    const [editValues, setEditValues] = useState({});
    const [addonValues, setAddonValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [priceHistory, setPriceHistory] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    
    // Channel management state
    const [addChannelDialog, setAddChannelDialog] = useState(false);
    const [editChannelDialog, setEditChannelDialog] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [channelForm, setChannelForm] = useState({
        displayName: '',
        channelType: 'theme',
        searchAliases: []
    });

    // Pagination and search state for channels
    const [channelPage, setChannelPage] = useState(0);
    const [channelRowsPerPage, setChannelRowsPerPage] = useState(25);
    const [channelSearchQuery, setChannelSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [pricingRes, channelsRes, themePacksRes] = await Promise.all([
                axios.get('/api/tv-packages-v2/pricing'),
                axios.get('/api/tv-packages-v2/admin/channels'),
                axios.get('/api/tv-packages-v2/admin/theme-packs')
            ]);
            
            setPackages(pricingRes.data.packages || []);
            setPriceTable(pricingRes.data.priceTable || {});
            setAddonPricing(pricingRes.data.addonPricing || {});
            setAddonValues(pricingRes.data.addonPricing || {});
            setChannels(channelsRes.data.channels || []);
            setThemePacks(themePacksRes.data.themePacks || []);
        } catch (error) {
            console.error('Error fetching V2 pricing data:', error);
            setError('Failed to load pricing data');
        }
        setLoading(false);
    };

    const fetchHistory = async () => {
        try {
            const response = await axios.get('/api/tv-packages-v2/pricing-history');
            setPriceHistory(response.data.history || []);
            setHistoryDialog(true);
        } catch (error) {
            console.error('Error fetching V2 history:', error);
            setError('Failed to load pricing history');
        }
    };

    const handleEditPackage = (pkg) => {
        setEditingPackage(pkg.package_key);
        setEditValues({ ...editValues, [pkg.package_key]: pkg.price });
    };

    const handleSavePackage = async (packageKey) => {
        const newPrice = parseFloat(editValues[packageKey]);
        
        if (isNaN(newPrice) || newPrice < 0) {
            setError('Please enter a valid price');
            return;
        }

        setSaving(true);
        try {
            await axios.put(`/api/tv-packages-v2/pricing/${packageKey}`, { price: newPrice });
            setSuccess('Package price updated successfully');
            setEditingPackage(null);
            fetchData(); // Refresh data
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating V2 price:', error);
            setError('Failed to update price');
        }
        setSaving(false);
    };

    const handleSaveAddon = async (addonType) => {
        const newPrice = parseFloat(addonValues[addonType]);
        
        if (isNaN(newPrice) || newPrice < 0) {
            setError('Please enter a valid price');
            return;
        }

        setSaving(true);
        try {
            await axios.put('/api/tv-packages-v2/addon-pricing', { 
                addonType, 
                price: newPrice 
            });
            setSuccess('Add-on price updated successfully');
            setEditingAddon(false);
            fetchData(); // Refresh data
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating V2 addon price:', error);
            setError('Failed to update add-on price');
        }
        setSaving(false);
    };

    const handleAddChannel = async () => {
        if (!channelForm.displayName || !channelForm.channelType) {
            setError('Display name and type are required');
            return;
        }

        setSaving(true);
        try {
            await axios.post('/api/tv-packages-v2/admin/channels', {
                channelKey: channelForm.displayName.toLowerCase().replace(/[^a-z0-9]/g, ''),
                displayName: channelForm.displayName,
                channelType: channelForm.channelType,
                searchAliases: channelForm.searchAliases
            });
            
            setSuccess('Channel added successfully');
            setAddChannelDialog(false);
            setChannelForm({ displayName: '', channelType: 'theme', searchAliases: [] });
            fetchData();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error adding channel:', error);
            if (error.response?.status === 409) {
                setError('Channel already exists');
            } else {
                setError('Failed to add channel');
            }
        }
        setSaving(false);
    };

    const handleEditChannel = (channel) => {
        setSelectedChannel(channel);
        setChannelForm({
            displayName: channel.display_name,
            channelType: channel.channel_type,
            searchAliases: channel.search_aliases || []
        });
        setEditChannelDialog(true);
    };

    const handleUpdateChannel = async () => {
        if (!channelForm.displayName || !channelForm.channelType) {
            setError('Display name and type are required');
            return;
        }

        setSaving(true);
        try {
            await axios.put(`/api/tv-packages-v2/admin/channels/${selectedChannel.channel_id}`, {
                displayName: channelForm.displayName,
                channelType: channelForm.channelType,
                searchAliases: channelForm.searchAliases,
                isActive: true
            });
            
            setSuccess('Channel updated successfully');
            setEditChannelDialog(false);
            setSelectedChannel(null);
            setChannelForm({ displayName: '', channelType: 'theme', searchAliases: [] });
            fetchData();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating channel:', error);
            setError('Failed to update channel');
        }
        setSaving(false);
    };

    const handleDeleteChannel = async (channelId) => {
        if (!window.confirm('Are you sure you want to delete this channel? This action cannot be undone.')) {
            return;
        }

        setSaving(true);
        try {
            await axios.delete(`/api/tv-packages-v2/admin/channels/${channelId}`);
            setSuccess('Channel deleted successfully');
            fetchData();
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error deleting channel:', error);
            setError('Failed to delete channel');
        }
        setSaving(false);
    };

    const formatPrice = (price) => {
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    // Filter channels based on search query
    const filteredChannels = channels.filter(channel => {
        if (!channelSearchQuery) return true;
        const query = channelSearchQuery.toLowerCase();
        return (
            channel.display_name.toLowerCase().includes(query) ||
            channel.channel_key.toLowerCase().includes(query) ||
            channel.channel_type.toLowerCase().includes(query) ||
            (channel.search_aliases && channel.search_aliases.some(alias => 
                alias.toLowerCase().includes(query)
            )) ||
            (channel.theme_packs && channel.theme_packs.some(pack => 
                pack.toLowerCase().includes(query)
            ))
        );
    });

    // Get paginated channels
    const paginatedChannels = filteredChannels.slice(
        channelPage * channelRowsPerPage,
        channelPage * channelRowsPerPage + channelRowsPerPage
    );

    // Handle pagination change
    const handleChannelPageChange = (event, newPage) => {
        setChannelPage(newPage);
    };

    const handleChannelRowsPerPageChange = (event) => {
        setChannelRowsPerPage(parseInt(event.target.value, 10));
        setChannelPage(0);
    };

    // Clear search
    const handleClearSearch = () => {
        setChannelSearchQuery('');
        setChannelPage(0);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LiveTvIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                TV Package Pricing & Channels V2
                <NewIcon sx={{ ml: 2, color: 'secondary.main' }} />
            </Typography>

            {/* V2 Features Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>New V2 Features:</strong> Channel management, proper display names, search aliases, and improved admin controls
                </Typography>
            </Alert>

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Package Pricing" icon={<MoneyIcon />} iconPosition="start" />
                    <Tab label="Add-on Pricing" icon={<SettingsIcon />} iconPosition="start" />
                    <Tab label="Channel Manager" icon={<ManageIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {tabValue === 0 && (
                <>
                    {/* Package Pricing Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Package Name</TableCell>
                                    <TableCell align="center">Theme Packs</TableCell>
                                    <TableCell align="center">Premium Channels</TableCell>
                                    <TableCell align="right">Current Price</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((pkg) => (
                                    <TableRow key={pkg.package_key}>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="medium">
                                                {pkg.package_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {pkg.package_key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {pkg.theme_count > 0 ? (
                                                <Chip 
                                                    label={pkg.theme_count} 
                                                    size="small" 
                                                    color="primary"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {pkg.premium_count > 0 ? (
                                                <Chip 
                                                    label={pkg.premium_count} 
                                                    size="small" 
                                                    color="secondary"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {editingPackage === pkg.package_key ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={editValues[pkg.package_key] || ''}
                                                    onChange={(e) => setEditValues({
                                                        ...editValues,
                                                        [pkg.package_key]: e.target.value
                                                    })}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ width: 120 }}
                                                />
                                            ) : (
                                                <Typography variant="h6" color="primary">
                                                    {formatPrice(pkg.price)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {editingPackage === pkg.package_key ? (
                                                <>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleSavePackage(pkg.package_key)}
                                                        disabled={saving}
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="default"
                                                        onClick={() => setEditingPackage(null)}
                                                        disabled={saving}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditPackage(pkg)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            startIcon={<HistoryIcon />}
                            onClick={fetchHistory}
                        >
                            View Price History
                        </Button>
                    </Box>
                </>
            )}

            {tabValue === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Theme Pack Add-on Pricing
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Price per additional theme pack beyond package limit
                                </Typography>
                                
                                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {editingAddon ? (
                                        <>
                                            <TextField
                                                label="Price per Theme Pack"
                                                type="number"
                                                value={addonValues.theme_pack || ''}
                                                onChange={(e) => setAddonValues({
                                                    ...addonValues,
                                                    theme_pack: e.target.value
                                                })}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                }}
                                                fullWidth
                                            />
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleSaveAddon('theme_pack')}
                                                disabled={saving}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton
                                                color="default"
                                                onClick={() => {
                                                    setEditingAddon(false);
                                                    setAddonValues(addonPricing);
                                                }}
                                                disabled={saving}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h4" color="primary">
                                                {formatPrice(addonPricing.theme_pack || 10)}
                                            </Typography>
                                            <Typography variant="body1">
                                                per theme pack
                                            </Typography>
                                            <IconButton
                                                color="primary"
                                                onClick={() => setEditingAddon(true)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    How Add-on Pricing Works
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText
                                            primary="Customer selects channels"
                                            secondary="System determines required theme packs"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Find best base package"
                                            secondary="Package with closest match to requirements"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Calculate add-ons"
                                            secondary="Additional theme packs × add-on price"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Total price"
                                            secondary="Base package + add-on charges"
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Note:</strong> The V2 system automatically calculates the best value for customers by comparing 
                                all package combinations including add-ons. Channel data is now stored in the database for easier management.
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            )}

            {tabValue === 2 && (
                <>
                    {/* Channel Management Header */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Channel Management 
                            <Chip 
                                label={`${filteredChannels.length}${filteredChannels.length !== channels.length ? ` of ${channels.length}` : ''} channels`}
                                size="small" 
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAddChannelDialog(true)}
                        >
                            Add Channel
                        </Button>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, position: 'relative' }}>
                            <SearchIcon sx={{ position: 'absolute', left: 12, color: 'text.secondary' }} />
                            <InputBase
                                placeholder="Search channels by name, type, theme packs, or aliases..."
                                value={channelSearchQuery}
                                onChange={(e) => setChannelSearchQuery(e.target.value)}
                                sx={{
                                    pl: 6,
                                    pr: channelSearchQuery ? 6 : 2,
                                    py: 1.5,
                                    width: '100%',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    '&:focus': {
                                        borderColor: 'primary.main'
                                    }
                                }}
                            />
                            {channelSearchQuery && (
                                <IconButton
                                    sx={{ position: 'absolute', right: 4 }}
                                    onClick={handleClearSearch}
                                    size="small"
                                >
                                    <ClearIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Channel Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Theme Packs</TableCell>
                                    <TableCell>Search Aliases</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedChannels.map((channel) => (
                                    <TableRow key={channel.channel_id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {channel.display_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Key: {channel.channel_key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={channel.channel_type} 
                                                size="small"
                                                color={
                                                    channel.channel_type === 'premium' ? 'secondary' :
                                                    channel.channel_type === 'core' ? 'success' : 'primary'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {channel.theme_packs && channel.theme_packs.length > 0 ? (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {channel.theme_packs.slice(0, 2).map((pack, idx) => (
                                                        <Chip key={idx} label={pack} size="small" variant="outlined" />
                                                    ))}
                                                    {channel.theme_packs.length > 2 && (
                                                        <Typography variant="caption">
                                                            +{channel.theme_packs.length - 2} more
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {channel.search_aliases && channel.search_aliases.length > 1 ? (
                                                <Typography variant="caption">
                                                    {channel.search_aliases.slice(1, 3).join(', ')}
                                                    {channel.search_aliases.length > 3 && '...'}
                                                </Typography>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditChannel(channel)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteChannel(channel.channel_id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={filteredChannels.length}
                        page={channelPage}
                        onPageChange={handleChannelPageChange}
                        rowsPerPage={channelRowsPerPage}
                        onRowsPerPageChange={handleChannelRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        showFirstButton
                        showLastButton
                        sx={{ borderTop: 1, borderColor: 'divider' }}
                    />
                </>
            )}

            {/* Add Channel Dialog */}
            <Dialog open={addChannelDialog} onClose={() => setAddChannelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Channel</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Display Name"
                        value={channelForm.displayName}
                        onChange={(e) => setChannelForm({ ...channelForm, displayName: e.target.value })}
                        margin="normal"
                        placeholder="e.g., A&E, Xbox Game Pass"
                    />
                    
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Channel Type</InputLabel>
                        <Select
                            value={channelForm.channelType}
                            onChange={(e) => setChannelForm({ ...channelForm, channelType: e.target.value })}
                        >
                            <MenuItem value="core">Core</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                            <MenuItem value="theme">Theme</MenuItem>
                        </Select>
                    </FormControl>

                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={channelForm.searchAliases}
                        onChange={(event, newValue) => {
                            setChannelForm({ ...channelForm, searchAliases: newValue });
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Search Aliases"
                                placeholder="Add search terms"
                                margin="normal"
                                helperText="Press Enter to add search terms (e.g., A and E, A & E)"
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddChannelDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleAddChannel}
                        variant="contained"
                        disabled={saving || !channelForm.displayName}
                    >
                        {saving ? 'Adding...' : 'Add Channel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Channel Dialog */}
            <Dialog open={editChannelDialog} onClose={() => setEditChannelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Channel</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Display Name"
                        value={channelForm.displayName}
                        onChange={(e) => setChannelForm({ ...channelForm, displayName: e.target.value })}
                        margin="normal"
                    />
                    
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Channel Type</InputLabel>
                        <Select
                            value={channelForm.channelType}
                            onChange={(e) => setChannelForm({ ...channelForm, channelType: e.target.value })}
                        >
                            <MenuItem value="core">Core</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                            <MenuItem value="theme">Theme</MenuItem>
                        </Select>
                    </FormControl>

                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={channelForm.searchAliases}
                        onChange={(event, newValue) => {
                            setChannelForm({ ...channelForm, searchAliases: newValue });
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Search Aliases"
                                placeholder="Add search terms"
                                margin="normal"
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditChannelDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleUpdateChannel}
                        variant="contained"
                        disabled={saving || !channelForm.displayName}
                    >
                        {saving ? 'Updating...' : 'Update Channel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Price History Dialog */}
            <Dialog
                open={historyDialog}
                onClose={() => setHistoryDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Price Change History
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <List>
                        {priceHistory.map((record, index) => (
                            <React.Fragment key={record.id}>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">
                                                    {record.package_key}
                                                </Typography>
                                                <Chip
                                                    label={`${formatPrice(record.old_price)} → ${formatPrice(record.new_price)}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption">
                                                    Changed by: {record.changed_by_name || 'Unknown'}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption">
                                                    Date: {formatDate(record.changed_at)}
                                                </Typography>
                                                {record.change_reason && (
                                                    <>
                                                        <br />
                                                        <Typography variant="caption">
                                                            Reason: {record.change_reason}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                {index < priceHistory.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                        {priceHistory.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="No price changes recorded"
                                    secondary="Price history will appear here when changes are made"
                                />
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TvPackagePricingV2;